/**
 * Animation Utility Functions
 *
 * This module provides utility functions for puppet animation control,
 * including position application for reset/dock operations.
 *
 * Key concept: Motor degrees vs Bone rotation
 * - Motor degrees: 0° = relaxed (string puppet convention)
 * - Bone rotation: Depends on GLTF model rest pose (often T-pose)
 * - boneOffset: Converts motor degrees to bone rotation
 *   Formula: boneRotation = motorDegrees + boneOffset
 */

import type { MotorPositions, MotorBodyPart } from '../stores/puppetStore';
import type { PuppetModelRef } from '../components/3d/PuppetModel';

/**
 * All motor body parts in order
 */
export const MOTOR_BODY_PARTS: MotorBodyPart[] = [
  'head',
  'body',
  'leftArm',
  'rightArm',
  'leftLeg',
  'rightLeg',
];

/**
 * Convert degrees to radians for Three.js bone rotation
 */
export function degreesToRadians(degrees: number): number {
  return degrees * (Math.PI / 180);
}

/**
 * Apply rotation to a bone on the specified axis
 *
 * Applies motor degrees to bone rotation, accounting for bone offset.
 * The boneOffset compensates for GLTF model rest pose (e.g., T-pose arms).
 *
 * @param puppetRef - Reference to the PuppetModel component
 * @param bodyPart - The body part to rotate
 * @param motorDegrees - The motor position in degrees (0 = relaxed)
 * @param axis - The axis to rotate on ('x', 'y', or 'z')
 * @param boneOffset - Offset to add for GLTF rest pose compensation (default: 0)
 */
export function applyRotationToBone(
  puppetRef: PuppetModelRef,
  bodyPart: MotorBodyPart,
  motorDegrees: number,
  axis: 'x' | 'y' | 'z',
  boneOffset: number = 0,
  direction: 1 | -1 = 1,
  xOffset: number = 0,
  yOffset: number = 0,
  zOffset: number = 0
): void {
  const bone = puppetRef.getBoneByBodyPart(bodyPart);
  if (!bone) return;

  // Convert motor degrees to bone rotation degrees (apply direction and offset)
  // Formula: boneRotation = (motorDegrees * direction) + boneOffset
  const boneRotationDegrees = motorDegrees * direction + boneOffset;
  const radians = degreesToRadians(boneRotationDegrees);

  // Apply controlled axis rotation + static offsets on other axes
  if (axis === 'x') {
    bone.rotation.x = radians;
    // Apply static offsets on non-controlled axes if specified
    if (yOffset !== 0) bone.rotation.y = degreesToRadians(yOffset);
    if (zOffset !== 0) bone.rotation.z = degreesToRadians(zOffset);
  } else if (axis === 'y') {
    bone.rotation.y = radians;
    // Apply static offsets on non-controlled axes if specified
    if (xOffset !== 0) bone.rotation.x = degreesToRadians(xOffset);
    if (zOffset !== 0) bone.rotation.z = degreesToRadians(zOffset);
  } else {
    bone.rotation.z = radians;
    // Apply static offsets on non-controlled axes if specified
    if (xOffset !== 0) bone.rotation.x = degreesToRadians(xOffset);
    if (yOffset !== 0) bone.rotation.y = degreesToRadians(yOffset);
  }
}

/**
 * Motor configuration getter function type
 * Returns axis, optional boneOffset, direction, and static offsets for a body part
 */
export interface MotorConfigResult {
  axis: 'x' | 'y' | 'z';
  boneOffset?: number;
  direction?: 1 | -1;
  /** Static offset for X axis (applied regardless of controlled axis) */
  xOffset?: number;
  /** Static offset for Y axis (applied regardless of controlled axis) */
  yOffset?: number;
  /** Static offset for Z axis (applied regardless of controlled axis) */
  zOffset?: number;
}

/**
 * Apply positions to all puppet bones instantly (no animation)
 *
 * This function is used for Reset to Start and Dock Puppet operations.
 * It applies the given positions immediately to all body parts without
 * any interpolation or animation.
 *
 * @param puppetRef - Reference to the PuppetModel component
 * @param positions - The motor positions to apply
 * @param getConfigFn - Function to get the motor config (axis and boneOffset) for a body part
 * @returns True if positions were applied successfully
 *
 * @example
 * ```typescript
 * // Reset puppet to starting position
 * const startingPosition = usePuppetStore.getState().activePuppet?.startingPosition;
 * if (startingPosition && puppetRef.current) {
 *   applyPositions(puppetRef.current, startingPosition, (bp) => {
 *     const config = store.getMotorConfig(bp);
 *     return { axis: config?.axis ?? 'x', boneOffset: config?.boneOffset };
 *   });
 * }
 * ```
 */
export function applyPositions(
  puppetRef: PuppetModelRef | null,
  positions: MotorPositions,
  getConfigFn: (bodyPart: MotorBodyPart) => MotorConfigResult | 'x' | 'y' | 'z'
): boolean {
  if (!puppetRef?.skeleton) {
    return false;
  }

  for (const bodyPart of MOTOR_BODY_PARTS) {
    const motorDegrees = positions[bodyPart];
    const config = getConfigFn(bodyPart);

    // Support both old (axis only) and new (config object) signatures
    if (typeof config === 'string') {
      applyRotationToBone(
        puppetRef,
        bodyPart,
        motorDegrees,
        config,
        0,
        1,
        0,
        0,
        0
      );
    } else {
      applyRotationToBone(
        puppetRef,
        bodyPart,
        motorDegrees,
        config.axis,
        config.boneOffset ?? 0,
        config.direction ?? 1,
        config.xOffset ?? 0,
        config.yOffset ?? 0,
        config.zOffset ?? 0
      );
    }
  }

  return true;
}

/**
 * Create a position application function bound to a puppet ref
 *
 * This factory function creates a closure that captures the puppet ref
 * and axis getter, making it easier to use in control panel buttons.
 *
 * @param puppetRef - Reference to the PuppetModel component
 * @param getAxisFn - Function to get the rotation axis for a body part
 * @returns A function that applies positions to the puppet
 *
 * @example
 * ```typescript
 * const applyToModel = createPositionApplicator(
 *   puppetRef.current,
 *   (bp) => store.getMotorConfig(bp)?.axis ?? 'x'
 * );
 *
 * // Later, apply positions:
 * applyToModel(startingPosition);
 * applyToModel(dockingPosition);
 * ```
 */
export function createPositionApplicator(
  puppetRef: PuppetModelRef | null,
  getAxisFn: (bodyPart: MotorBodyPart) => 'x' | 'y' | 'z'
): (positions: MotorPositions) => boolean {
  return (positions: MotorPositions) => {
    return applyPositions(puppetRef, positions, getAxisFn);
  };
}

/**
 * Default axis getter - returns 'x' for all body parts
 *
 * Use this when motor config is not available.
 */
export function getDefaultAxis(): 'x' | 'y' | 'z' {
  return 'x';
}
